/**
 * /two-factor — second-factor challenge.
 *
 * `flow` search param disambiguates:
 *   - `sign-in`: user already authenticated with password; we need
 *     TOTP or backup code. Reads `useSignIn()`, calls
 *     `signIn.attemptSecondFactor({ strategy: 'totp' | 'backup_code' })`.
 *   - `enroll`: post-TOTP-enrollment smoke test (Settings → Security).
 *     Stage 3 keeps this simple — enrollment lives entirely inside
 *     Settings, so `flow=enroll` lands users back at the settings
 *     page after a successful attempt. Today it also uses
 *     `attemptSecondFactor` through `useSignIn()`, but practically
 *     Settings handles enrollment inline and doesn't redirect here.
 */

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OTPField, OTPFieldInput } from '@/components/ui/otp-field';
import { activateSession, attemptBackupCode, attemptTotp } from '@/lib/auth';
import { useSignIn } from '@clerk/react/legacy';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useRef, useState } from 'react';

export const Route = createFileRoute('/_auth/two-factor')({
  component: TwoFactorPage,
  validateSearch: (search): { flow?: 'sign-in' | 'enroll'; redirect?: string } => ({
    flow: search.flow === 'enroll' ? 'enroll' : 'sign-in',
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
});

function TwoFactorPage(): ReactElement {
  const { signIn, setActive, isLoaded } = useSignIn();
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();

  const [mode, setMode] = useState<'totp' | 'backup'>('totp');
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | undefined>(undefined);

  const submittingRef = useRef(false);

  async function finish(attempt: typeof attemptTotp | typeof attemptBackupCode, value: string) {
    if (!isLoaded || !signIn || !setActive) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setFormError(undefined);

    const result = await attempt(signIn, value);

    if (result.kind === 'error') {
      setFormError(result.fieldErrors.code ?? result.formError);
      if (mode === 'totp') setCode('');
      if (mode === 'backup') setBackupCode('');
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    await activateSession({ setActive, sessionId: result.sessionId, redirectUrl: redirect ?? '/' });
  }

  function onTotpChange(value: string) {
    setCode(value);
    if (value.length === 6) {
      void finish(attemptTotp, value);
    }
  }

  function onBackupSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!backupCode) return;
    void finish(attemptBackupCode, backupCode);
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center p-6 text-sm text-[var(--color-mute)]">
        Loading…
      </div>
    );
  }

  if (!signIn || signIn.status !== 'needs_second_factor') {
    return (
      <div className="space-y-3 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">No sign-in in progress</h1>
        <p className="text-sm text-[var(--color-mute)]">Start again from the sign-in page.</p>
        <Button onClick={() => navigate({ to: '/sign-in' })}>Sign in</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">Two-factor required</h1>
        <p className="text-sm text-[var(--color-mute)]">
          {mode === 'totp'
            ? 'Enter the 6-digit code from your authenticator app.'
            : 'Enter one of the 10 backup codes you saved when enabling 2FA.'}
        </p>
      </header>
      {mode === 'totp' ? (
        <div className="flex flex-col items-center gap-4">
          <OTPField length={6} value={code} onValueChange={onTotpChange} disabled={submitting}>
            {Array.from({ length: 6 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length OTP slots
              <OTPFieldInput key={i} />
            ))}
          </OTPField>
        </div>
      ) : (
        <form className="space-y-3" onSubmit={onBackupSubmit}>
          <Input
            autoFocus
            placeholder="abcd-efgh"
            value={backupCode}
            onChange={(event) => setBackupCode((event.target as HTMLInputElement).value)}
            aria-invalid={formError ? true : undefined}
          />
          <Button type="submit" className="w-full" disabled={submitting || !backupCode}>
            {submitting ? 'Verifying…' : 'Verify backup code'}
          </Button>
        </form>
      )}
      {formError && (
        <div
          className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
          role="alert"
        >
          {formError}
        </div>
      )}
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => {
            setMode((prev) => (prev === 'totp' ? 'backup' : 'totp'));
            setFormError(undefined);
            setCode('');
            setBackupCode('');
          }}
          className="text-sm font-medium text-[var(--color-accent-brand)] hover:underline"
        >
          {mode === 'totp' ? 'Use a backup code' : 'Use authenticator app'}
        </button>
      </div>
    </div>
  );
}
